import { inject } from '@angular/core';
import { PlanGroup } from '@loyalty-v3/libs';
import { Actions, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { init } from '@stores/plan/plan.actions';
import { selectProfile } from '@stores/profile/profile.selectors';
import { selectIfDefined } from '@stores/utils/selects.operators';
import { PlanWebService } from '@webservices/plan.webservice';
import { Observable, ObservableInput, combineLatest, filter, forkJoin, map, of, switchMap } from 'rxjs';

export const loadPlan = createEffect(
  (actions$ = inject(Actions), planWebservice = inject(PlanWebService), store = inject(Store)) =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() => selectIfDefined(store, selectProfile)),
      filter((profile) => !!profile.id && !!profile.planGroupId),
      switchMap((profile) => {
        const planGroupId = profile.planGroupId as string;
        const children = profile.children ?? [];

        if (children.length <= 0 || children.every((child) => child.planGroupId === null)) {
          return planWebservice.getPlanGroup(planGroupId).pipe(
            switchMap((userPlanGroup) => {
              const relatedPlanGroupId = userPlanGroup?.relatedGroupId;

              return combineLatest([
                of(userPlanGroup),
                relatedPlanGroupId ? planWebservice.getPlanGroup(relatedPlanGroupId) : of(undefined),
              ]);
            })
          );
        }

        const childrenPlanGroupsIds = [...new Set(children.map((child) => child.planGroupId))];

        const planGroupRequests: Record<string, Observable<PlanGroup>> = {};
        for (const planGroupId of childrenPlanGroupsIds) {
          if (planGroupId) planGroupRequests[planGroupId] = planWebservice.getPlanGroup(planGroupId);
        }

        return combineLatest([
          planWebservice.getPlanGroup(planGroupId),
          forkJoin<Record<string, ObservableInput<PlanGroup>>>(planGroupRequests),
        ]).pipe(
          switchMap(([userPlanGroup, childrenPlanGroups]) => {
            const relatedPlanGroupId = userPlanGroup?.relatedGroupId;

            return combineLatest([
              of(userPlanGroup),
              relatedPlanGroupId ? planWebservice.getPlanGroup(relatedPlanGroupId) : of(undefined),
              of(childrenPlanGroups),
            ]);
          })
        );
      }),
      map(([userPlanGroup, relatedPlanGroup, childrenPlanGroups]) =>
        init({ planGroups: { userPlanGroup, childrenPlanGroups, relatedPlanGroup } })
      )
    ),
  { functional: true }
);
